import CircularProgress from '@material-ui/core/CircularProgress';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import LockIcon from '@material-ui/icons/Lock';
import { publicSvgPath } from '@orientaction/utils';
import { ChartBar } from '@phosphor-icons/react';
import { Fragment, ReactNode } from 'react';
import { formatTestDuration } from '../../utils/formatTestDuration';
import RedesignImage from '../RedesignImage';
import { useMinorTestCardStyles } from './MinorTestCard.styles';

export type MinorTestCardStatus = 'todo' | 'progress' | 'done';

export type MinorTestCardProps = {
  title: string;
  image?: string;
  thumbnail?: string;
  time?: string;
  description?: string;
  status?: MinorTestCardStatus;
  buttonLabel: string;
  showResultIcon?: boolean;
  loading?: boolean;
  disabled?: boolean;
  locked?: boolean;
  durationFallback?: string;
  onAction: () => void;
  className?: string;
};

const renderRegisteredTitle = (title: string, registeredMarkClass: string) => {
  const parts = title.split('®');
  let offset = 0;

  return parts.map((part, partIndex) => {
    const key = `title-part-${offset}`;
    offset += part.length + 1;

    return (
      <Fragment key={key}>
        {part}
        {partIndex < parts.length - 1 && <span className={registeredMarkClass}>®</span>}
      </Fragment>
    );
  });
};

const MinorTestCard = ({
  title,
  image,
  thumbnail,
  time,
  description,
  status = 'todo',
  buttonLabel,
  showResultIcon = false,
  loading = false,
  disabled = false,
  locked = false,
  durationFallback,
  onAction,
  className,
}: MinorTestCardProps) => {
  const classes = useMinorTestCardStyles();
  const imageSrc = thumbnail || image || `${publicSvgPath}/QR.png`;

  const renderStatus = (badgeStatus: 'done' | 'progress') => (
    <span
      className={`${classes.statusBadge} ${
        badgeStatus === 'done' ? classes.statusDone : classes.statusProgress
      }`}>
      <span className={classes.statusDot} />
      {badgeStatus === 'done' ? 'Test fait' : 'Test en cours'}
    </span>
  );

  let statusBadge: ReactNode = null;
  if (status === 'progress') {
    statusBadge = renderStatus('progress');
  } else if (status === 'done') {
    statusBadge = renderStatus('done');
  }

  return (
    <button
      type="button"
      className={`${classes.testCard} ${classes.minorCard} ${className || ''}`}
      disabled={disabled || loading}
      onClick={onAction}
      aria-label={`${buttonLabel} ${title}`}>
      <div className={`${classes.imageWrap} ${classes.minorImageWrap}`}>
        <RedesignImage
          className={classes.image}
          src={imageSrc}
          alt=""
          onError={event => {
            (event.currentTarget as HTMLImageElement).src = `${publicSvgPath}/QR.png`;
          }}
        />
        {statusBadge}
      </div>
      <div className={classes.cardBody}>
        <div className={classes.cardText}>
          <h3 className={classes.cardTitle}>
            {renderRegisteredTitle(title, classes.registeredMark)}
          </h3>
          {description && (
            <div
              className={classes.cardDescription}
              dangerouslySetInnerHTML={{ __html: description }}
            />
          )}
        </div>
        <div className={classes.timeRow}>
          <AccessTimeIcon className={classes.timeIcon} />
          <span>{formatTestDuration(time, durationFallback)}</span>
        </div>
      </div>
      <span className={classes.cardButton} aria-hidden="true">
        {loading ? (
          <CircularProgress size={16} className={classes.cardButtonIcon} />
        ) : (
          showResultIcon && (
            <ChartBar
              className={classes.cardButtonIcon}
              size={16}
              weight="regular"
              aria-hidden="true"
            />
          )
        )}
        {buttonLabel}
      </span>
      {locked && (
        <div className={classes.lockOverlay} aria-hidden="true">
          <span className={classes.lockIconWrap}>
            <LockIcon className={classes.lockIcon} />
          </span>
        </div>
      )}
    </button>
  );
};

export default MinorTestCard;
